import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import {withTranslation} from 'react-i18next';
import {Redirect} from 'react-router-dom';
import BaseContainer from '../baseContainers/BaseContainer';
import ActionButton from '../components/ActionButton';
import InputPasswordComponent from '../components/inputs/InputPasswordComponent';
import SimpleReactValidator from '../components/validator';
import BlockUi from '../components/waitPanel/BlockUi';
import AuthService from '../services/AuthService';
import SettingService from '../services/SettingService';
import UserService from '../services/UserService';
import {CustomMessages} from '../components/CustomMessages';

class ChangePasswordContainer extends BaseContainer {
    constructor(props) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handlePasswordsMatch = this.handlePasswordsMatch.bind(this);
        this.authService = new AuthService();
        this.userService = new UserService();
        this.settingService = new SettingService();
        this.state = {
            username: '',
            password: '',
            redirectToReferrer: true,
            authValid: true,
            showCaptcha: false,
            captchaPreventAction: false,
            maxLength: 16,
            minLength: 8,
            lowerCaseCount: 1,
            upperCaseCount: 1,
            numbersCount: 1,
            specialCharactersCount: 1,
            newPassword: '',
            repeatPassword: '',
            showValidationMessage: false,
            validatePasswords: false,
        };
        this.authValidValidator = new SimpleReactValidator(
            {
                validators: {
                    auth: {
                        // name the rule
                        message: ':attribute',
                        rule: (val, params, validator) => {
                            return this.state.authValid;
                        }, // optional
                        required: true,
                    },
                },
            },
            props.t
        );
    }

    translate(key) {
        const {t} = this.props;
        return t(key);
    }

    componentDidMount() {
        super.componentDidMount();
        const values = queryString.parse(this.props.location.search);
        this.targetLocation = values.location;
        this.settingService
            .getPasswordParamsPublic()
            .then((resp) => {
                this.setState(
                    {
                        maxLength: resp['PASSWORD_MAX_LENGTH'],
                        minLength: resp['PASSWORD_MIN_LENGTH'],
                        lowerCaseCount: resp['PASSWORD_LOWERCASE_COUNT'],
                        upperCaseCount: resp['PASSWORD_UPPERCASE_COUNT'],
                        numbersCount: resp['PASSWORD_NUMBERS_COUNT'],
                        specialCharactersCount: resp['PASSWORD_SPECIAL_CHARS_COUNT'],
                    },
                    () => this.unblockUi()
                );
            })
            .catch((err) => this.unblockUi());
    }

    persistMessage(severity, summary, detail) {
        this.saveCookie(
            'login-container',
            JSON.stringify({
                severity,
                summary,
                detail,
            })
        );
    }

    // handleChange(e) {
    //     this.setState({
    //         [e.target.name]: e.target.value,
    //     });
    // }

    handleFormSubmit(e) {
        e.preventDefault();
        const {t} = this.props;
        if (this.authValidValidator.allValid() && this.handlePasswordsMatch()) {
            this.blockUi();
            this.userService
                .verifyTokenAndSendNewPassword(this.props?.match?.params?.token, {newPassword: this.state.newPassword})
                .then((res) => {
                    this.blockUi();
                    this.persistMessage('success', '', t('password.setPasswordSuccess'));
                    window.location.href = '/#/';
                })
                .catch((err) => {
                    this.unblockUi();
                    this.showErrorMessage(t('error.setPasswordError'));
                });
        } else {
            this.authValidValidator.showMessages();
            // rerender to show messages for the first time
            this.setState({
                valid: false,
                validatePasswords: true,
            });
            this.forceUpdate();
        }
    }

    handlePasswordsMatch() {
        const {newPassword, repeatPassword} = this.state;
        if (newPassword.length > 0 && repeatPassword.length > 0 && newPassword !== repeatPassword) {
            this.setState({showValidationMessage: true});
            return false;
        } else {
            this.setState({showValidationMessage: false});
            return true;
        }
    }

    render() {
        if (this.authService.loggedIn()) {
            return this.renderAfterAuth();
        } else {
            return this.renderBeforeAuth();
        }
    }

    renderAfterAuth() {
        const {redirectToReferrer} = this.state;
        if (redirectToReferrer === true) {
            return <Redirect to={this.targetLocation ? this.targetLocation : '/start'} />;
        }
        return <Redirect to={'/start'} />;
    }

    renderBeforeAuth() {
        const {t} = this.props;
        return (
            <React.Fragment>
                <BlockUi
                    tag='div'
                    blocking={this.state.blocking || this.state.loading}
                    loader={this.loader}
                    rendered={!this.props.embedded}
                >
                    <div id='main-login'>
                        <CustomMessages id='custom-messages' ref={(el) => (this.messages = el)} rendered={!this.props.embedded} />
                        <div className='login-background-container'>
                            <img src='/images/login-moya-background.svg' className='login-background-moya' alt='logo moya' />
                            <form
                                className='login-container'
                                onSubmit={(e) => {
                                    if (!this.state.captchaPreventAction) {
                                        this.handleFormSubmit(e);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (!this.state.captchaPreventAction) {
                                            this.handleFormSubmit();
                                        }
                                    }
                                }}
                                //avoid setting red border just after the page has loaded (red border because of undefined required value in FF)
                                noValidate
                            >
                                <div className='login-moya-logo-container'>
                                    <img src='/images/login-moya-logo.svg' className='login-moya-logo' alt='logo moya' />
                                </div>
                                <div className='login-welcome-text-container'>
                                    <span className='login-welcome-text'>{t('password.setPasswordInstruction')}</span>
                                </div>
                                <div className='login-form-container'>
                                    <div className='reset-password-subtitle'>
                                        {`${t('password.extended_password')}`}
                                    </div>
                                    <InputPasswordComponent
                                        id='newPassword'
                                        // eslint-disable-next-line max-len
                                        label={t('password.newPassword')}
                                        name='newPassword'
                                        value={this.state.newPassword}
                                        validator={this.authValidValidator}
                                        onChange={this.handleChange}
                                        viewMode='EDIT'
                                        validators={`required|extended_password:${this.state.minLength}:${this.state.maxLength}:${this.state.lowerCaseCount}:${this.state.upperCaseCount}:${this.state.numbersCount}:${this.state.specialCharactersCount}`}
                                        colClass='col-12 '
                                        stateField=''
                                        onAfterStateChange={this.handlePasswordsMatch}
                                    />
                                    <InputPasswordComponent
                                        id='repeatPassword'
                                        label={t('password.repeatNewPassword')}
                                        name='repeatPassword'
                                        value={this.state.repeatPassword}
                                        validator={this.authValidValidator}
                                        onChange={this.handleChange}
                                        viewMode='EDIT'
                                        validators={`required`}
                                        colClass='col-12'
                                        stateField=''
                                        feedback={false}
                                        onAfterStateChange={this.handlePasswordsMatch}
                                    />
                                    {this.state.validatePasswords && this.state.showValidationMessage === true ? (
                                        <div className='srv-validation-message' id={`${'id'}-error`}>
                                            {t('validator.equals')
                                                .replace(':fieldName', t('password.repeatNewPassword'))
                                                .replace(':attribute', t('password.newPassword'))}
                                        </div>
                                    ) : null}
                                    <ActionButton
                                        label={t('password.send')}
                                        variant='login-button set-password-button blue'
                                        handleClick={this.handleFormSubmit}
                                        disabled={this.state.captchaPreventAction}
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                </BlockUi>
            </React.Fragment>
        );
    }
}

ChangePasswordContainer.propTypes = {
    onAfterLogin: PropTypes.func,
};
export default withTranslation()(ChangePasswordContainer);
